import { ChevronDownIcon } from "@ui/components/icons/ChevronDownIcon";
import { getSelectCustomStyles } from "@ui/components/inputs/select/getSelectCustomStyles";
import { Maybe, MaybeUndef, emptyFunction } from "shared";
import ReactSelect, {
  ActionMeta,
  MenuPlacement,
  MenuPosition,
  SingleValue,
  StylesConfig,
} from "react-select";
import { ColorValue } from "@ui/types/enums/ColorValue";
import { useState } from "react";

function CustomDropdownIndicator() {
  return (
    <div style={{ height: 24, paddingRight: 8 }}>
      <ChevronDownIcon colorValue={ColorValue.Primary} />
    </div>
  );
}

type Option = { label: string; value: string };

type Props = {
  customStyles?: StylesConfig<{ label: string; value: string }, false>;
  defaultValue?: MaybeUndef<Option>;
  hasError?: boolean;
  height?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  menuPlacement?: MenuPlacement;
  menuPosition?: MenuPosition;
  onChange: (value: SingleValue<Option>, action: ActionMeta<Option>) => void;
  options: Array<Option>;
  placeholder: string;
  showCursor?: boolean;
  value?: Maybe<Option>;
  width?: number | string;
};

export function SelectLoading({
  placeholder,
  width,
}: {
  placeholder: string;
  width?: number | string;
}) {
  return (
    <Select
      isLoading
      onChange={emptyFunction}
      options={[]}
      placeholder={placeholder}
      width={width}
    />
  );
}

/**
 * A react-select component with custom styling.
 */
export function Select({
  customStyles,
  defaultValue,
  hasError = false,
  height,
  isDisabled = false,
  isLoading = false,
  menuPlacement = "auto",
  menuPosition,
  onChange,
  options,
  placeholder,
  value,
  showCursor,
  width,
}: Props): JSX.Element {
  const [ref, setRef] = useState<Maybe<HTMLDivElement>>(null);

  return (
    <div ref={(r) => setRef(r)} style={{ width }}>
      <ReactSelect
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          IndicatorSeparator: () => null,
        }}
        defaultValue={defaultValue}
        isDisabled={isDisabled}
        isLoading={isLoading}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        menuPortalTarget={ref}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        styles={{
          ...getSelectCustomStyles({
            hasError,
            height: height ?? 52,
            showCursor,
          }),
          ...customStyles,
        }}
        value={value}
      />
    </div>
  );
}
